import { useEffect } from "react";

// Installed as a PWA there is no browser chrome around this app: Android's
// back gesture is the only way back, and by default it is also the way out.
// With nothing of its own in the history stack, one back press would close
// the app instead of the sheet that is open on screen.
//
// A component that is only ever mounted while it is "open" (the task sheet)
// calls this once, unconditionally, for as long as it exists. It pushes one
// history entry on mount, so the first back press consumes that entry and
// fires `onClose` instead of leaving the app.
//
// Closed from inside instead (a tap outside, a button), the pushed entry is
// still sitting there unconsumed, so it is popped on unmount. The two ways of
// closing never leave the history stack one deep.
//
// Two things make the push and the listener wait:
//
//   - StrictMode mounts every effect, unmounts it and mounts it again in
//     development. A synchronous push is popped by that first cleanup's
//     history.back(), and the popstate that raises would close the sheet the
//     second mount had just opened. Waiting a microtask lets the first
//     cleanup cancel before anything is pushed.
//   - A sheet that closes calls history.back(), which lands later. If another
//     sheet opens meanwhile, the late pop would close the new one. So the next
//     sheet waits for that pop to land (or a short timeout) before it pushes.
let settling: Promise<void> = Promise.resolve();

export function useCloseOnBack(onClose: () => void) {
  useEffect(() => {
    let alive = true;
    let pushed = false;
    const onPopState = () => onClose();
    settling.then(() => {
      if (!alive) return;
      history.pushState({ mcSheet: true }, "");
      pushed = true;
      window.addEventListener("popstate", onPopState);
    });
    return () => {
      alive = false;
      window.removeEventListener("popstate", onPopState);
      if (pushed && history.state?.mcSheet) {
        settling = new Promise<void>((resolve) => {
          const done = () => { window.removeEventListener("popstate", done); window.clearTimeout(t); resolve(); };
          const t = window.setTimeout(done, 300);
          window.addEventListener("popstate", done);
        });
        history.back();
      }
    };
    // Deliberately once, on mount/unmount, not re-run when `onClose` changes
    // identity. The entry belongs to the sheet's lifetime, not to a
    // particular render of the callback that closes it.
  }, []);
}
